import React, { useState } from "react";
import { Modal } from "./Modal";

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageName?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  vehicle: string;
  message: string;
}

export function LeadFormModal({
  isOpen,
  onClose,
  packageName,
}: LeadFormModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    phone: "",
    vehicle: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        import.meta.env.VITE_CONTACT_WORKER_URL || "http://localhost:8787",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            package: packageName,
          }),
        },
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          vehicle: "",
          message: "",
        });

        // Auto-close modal after 3 seconds
        setTimeout(() => {
          onClose();
          setIsSuccess(false);
        }, 3000);
      } else {
        setError(result.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "", phone: "", vehicle: "", message: "" });
    setError(null);
    setIsSuccess(false);
    onClose();
  };

  if (isSuccess) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="">
        <div style={{ textAlign: "center", padding: "4rem 2rem" }}>
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✅</div>
          <h2
            style={{
              fontSize: "2.5rem",
              color: "#28a745",
              marginBottom: "1rem",
            }}
          >
            Thank You!
          </h2>
          <p style={{ fontSize: "1.2rem", color: "#666", lineHeight: "1.6" }}>
            We'll contact you within 24 hours.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Get a Quote Or Inquire"
    >
      <form onSubmit={handleSubmit} style={{ padding: "1rem 0" }}>
        {error && (
          <div
            style={{
              background: "#f8d7da",
              color: "#721c24",
              padding: "1rem",
              borderRadius: "8px",
              marginBottom: "1.5rem",
              border: "1px solid #f5c6cb",
            }}
          >
            {error}
          </div>
        )}

        {/* Name Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem", flexWrap: "wrap", gap: "0.5rem" }}>
            <label
              htmlFor="name"
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "#333",
              }}
            >
              Name & surname
            </label>
            {packageName && (
              <span style={{
                fontSize: "0.56rem",
                color: "#000",
                fontWeight: 600,
                background: "#ffeb3b",
                padding: "0.25rem 0.5rem",
                borderRadius: "4px"
              }}>
                Inquiring about: {packageName}
              </span>
            )}
          </div>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name & surname"
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#395839";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Email Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="email"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#395839";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Phone Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="phone"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            Your Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Your phone number"
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#395839";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Vehicle Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="vehicle"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            Vehicle details
          </label>
          <input
            type="text"
            id="vehicle"
            name="vehicle"
            value={formData.vehicle}
            onChange={handleChange}
            placeholder="e.g., 2020 Toyota Hilux or Land Cruiser"
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#395839";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Message Field */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label
            htmlFor="message"
            style={{
              display: "block",
              fontSize: "1rem",
              fontWeight: 600,
              color: "#333",
              marginBottom: "0.5rem",
            }}
          >
            Your Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your vehicle and what you're looking for..."
            rows={6}
            required
            style={{
              width: "100%",
              padding: "1rem",
              border: "2px solid #e9ecef",
              borderRadius: "8px",
              fontSize: "1rem",
              transition: "all 0.3s ease",
              boxSizing: "border-box",
              resize: "vertical",
              minHeight: "120px",
              fontFamily: "inherit",
              color: "#333",
              backgroundColor: "#fff",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#395839";
              e.target.style.boxShadow = "0 0 0 3px rgba(57, 88, 57, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e9ecef";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: "1rem",
              background: "#f8f9fa",
              color: "#666",
              border: "2px solid #e9ecef",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: "1rem",
              background: isSubmitting
                ? "#999"
                : "#395839",
              color: "white",
              border: "none",
              borderRadius: "50px",
              fontSize: "1.1rem",
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              boxShadow: isSubmitting
                ? "none"
                : "0 4px 15px rgba(57, 88, 57, 0.3)",
              transition: "all 0.3s ease",
              opacity: isSubmitting ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(57, 88, 57, 0.4)";
                e.currentTarget.style.background = "#2d4629";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 15px rgba(57, 88, 57, 0.3)";
              e.currentTarget.style.background = "#395839";
            }}
          >
            {isSubmitting ? "Sending..." : "Submit"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
